import { useAuth } from '@/context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { RentalProduct } from '@/types/entities';
import { InventoryItem, DbInventoryItem } from '@/types/entities/inventory';
import { Database } from '@/types/supabase';
import { 
  mapDbInventoryItemToInventoryItem, 
  mapInventoryItemToDbInsert, 
  mapInventoryItemUpdateToDbUpdate 
} from '@/shared/mappers/inventoryMappers';

// Используем типы из унифицированной таблицы inventory
type InventoryInsert = Database['public']['Tables']['inventory']['Insert'];
type InventoryUpdate = Database['public']['Tables']['inventory']['Update'];

// Используем централизованные мапперы для всех операций с данными

export const useInventoryUnified = (filters?: Record<string, unknown>) => {
  const { user } = useAuth();
  
  // Упрощенная обработка фильтров для избежания циклической зависимости типов
  const processedFilters: Record<string, unknown> = { ...filters };
  // Удаляем несуществующие поля из фильтров
  if (filters?.rentalPointId) {
    delete processedFilters.rentalPointId; // Поле не существует в реальной схеме БД
  }
  
  const queryKey = ['inventory', { userId: user?.id, ...processedFilters }];

  const {
    data: inventoryItems = [], 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user?.id) return [];
      
      let query = supabaseWorker
        .from('inventory')
        .select('*')
        .eq('user_id', user.id);
      
      // Применяем фильтры с упрощенной типизацией для избежания циклической зависимости
      const filterEntries = Object.entries(processedFilters || {});
      for (const [key, value] of filterEntries) {
        if (value !== undefined && value !== null && value !== '') {
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            query = query.eq(key, value);
          }
        }
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // ИСПРАВЛЕНО: используем правильный маппер вместо processInventoryItem
      const rows = (data ?? []) as Database['public']['Tables']['inventory']['Row'][];
      return rows.map(item => mapDbInventoryItemToInventoryItem(item as unknown as DbInventoryItem));
    },
    enabled: !!user,
  });

  const queryClient = useQueryClient();

  // Заменяем useEntityOperations на прямые мутации
  const { mutate: createEntity, isPending: isCreating } = useMutation({
    mutationFn: async (item: Partial<InventoryItem>) => {
      console.log('[useInventoryUnified][create] Input item:', item);
      const dbItem = mapInventoryItemToDbInsert(item);
      console.log('[useInventoryUnified][create] Mapped dbItem:', dbItem);
      const { data, error } = await supabaseWorker
        .from('inventory')
        .insert([dbItem])
        .select()
        .single();
      if (error) {
        console.error('[useInventoryUnified][create] Supabase error:', error);
        console.error('[useInventoryUnified][create] Full error object:', JSON.stringify(error, null, 2));
        const err = error as { message?: string; code?: string; details?: string; hint?: string; statusCode?: number };
        console.error('[useInventoryUnified][create] Error details:', {
          message: err.message,
          code: err.code,
          details: err.details,
          hint: err.hint,
          statusCode: err.statusCode,
        });
        throw error;
      }
      console.log('[useInventoryUnified][create] Success, created:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error, variables) => {
      console.error('[useInventoryUnified][create] Mutation failed:', error, 'variables:', variables);
    }
  });

  const { mutate: updateEntity, mutateAsync: updateEntityAsync, isPending: isUpdating } = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<InventoryItem> }) => {
      const dbUpdates = mapInventoryItemUpdateToDbUpdate(updates);
      console.log('[useInventoryUnified][update] Payload:', { id, dbUpdates });
      const { data, error } = await supabaseWorker
        .from('inventory')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        console.error('[useInventoryUnified][update] Supabase error:', error, 'payload:', { id, dbUpdates });
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error, variables) => {
      console.error('[useInventoryUnified][update] Mutation failed:', error, 'variables:', variables);
    }
  });

  const { mutate: deleteEntity, isPending: isDeleting } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseWorker
        .from('inventory')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const duplicateInventoryMutation = useMutation({
    mutationFn: async ({ id, sku }: { id: string; sku?: string }) => {
      const { data, error } = await supabaseWorker.functions.invoke(
        `inventory-api/inventory/${id}/duplicate`,
        {
          body: sku ? { sku } : undefined
        }
      );

      if (error) {
        throw new Error(error.message ?? 'Не удалось выполнить дублирование инвентаря');
      }

      const payload = (data as { data?: DbInventoryItem | null } | null)?.data;

      if (!payload) {
        throw new Error('Ответ не содержит данных созданной копии инвентаря');
      }

      return mapDbInventoryItemToInventoryItem(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    inventory: inventoryItems || [],
    loading: isLoading,
    error,
    refetch,
    createInventoryItem: createEntity,
    // ВАЖНО: предоставляем async-версию, принимающую (id, updates), чтобы на неё можно было делать await
    updateInventoryItem: async (id: string, updates: Partial<InventoryItem>) => {
      return updateEntityAsync({ id, updates });
    },
    deleteInventoryItem: deleteEntity,
    duplicateInventoryItem: duplicateInventoryMutation.mutateAsync,
    isDuplicating: duplicateInventoryMutation.isPending,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
